import type { StaffAnalyticsSelection } from "@lafayette/schemas";
import { Link } from "react-router";

import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { cn } from "../../ui/cn";
import { countText, durationText, moneyText } from "../format";
import { changeText, differenceText } from "./explore-model";
import { screenLabel } from "./metric-catalog";
import { ShareBar } from "./ShareBar";
import { spanPhrase, type Zoom } from "./zoom-math";

// "In this selection": the four numbers of the window with the change against the period before, and where the time went by screen. A number
// that cannot be read (the customer chose not to share app analytics) is a dash and a reason, never a zero. The change is not drawn against a
// period that the 13-month limit cut short: it would compare unequal spans.

type Previous = NonNullable<StaffAnalyticsSelection["previous"]>;

/** The relative change as words, or `null` when either number is missing or the earlier one is zero. */
function percentChange(value: number | null, previous: number | null | undefined): string | null {
  if (value === null || previous === null || previous === undefined || previous === 0) return null;
  return changeText((value - previous) / previous);
}

function Stat({
  label,
  value,
  change,
}: Readonly<{ label: string; value: string; change: string | null }>) {
  return (
    <div className="grid content-start gap-0.5 rounded-xl bg-surface-sunken px-3.5 py-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="m-0 flex flex-wrap items-baseline gap-x-2">
        <span className="font-serif text-xl leading-7 font-medium whitespace-nowrap">{value}</span>
        {change === null ? null : (
          <span
            className={cn(
              "text-xs font-semibold",
              change.startsWith("−") ? "text-muted-foreground" : "text-success",
            )}
          >
            {change}
          </span>
        )}
      </dd>
    </div>
  );
}

function comparison(selection: StaffAnalyticsSelection, previous: Previous | null) {
  // A period that the 13-month limit cut short is not compared: the spans would be unequal.
  const usable = previous?.partial === false ? previous : null;
  return {
    sessions: percentChange(selection.sessions, usable?.sessions),
    time: percentChange(selection.timeInAppSeconds, usable?.timeInAppSeconds),
    requests: usable === null ? null : differenceText(selection.requests, usable.requests),
    paid: percentChange(selection.amountPaidMinor, usable?.amountPaidMinor),
  };
}

function appNumber(value: number | null, text: (value: number) => string): string {
  return value === null ? "—" : text(value);
}

function previousNote(selection: StaffAnalyticsSelection, spanText: string): string | null {
  if (selection.previousUnavailable === "RETENTION")
    return "The period before is older than 13 months, so app data is not kept for it.";
  if (selection.previousUnavailable === "NO_PERIOD")
    return "There is no period before the whole history.";
  if (selection.previous?.partial === true)
    return "The period before reaches past 13 months, so only part of it exists. No change is shown.";
  return selection.previous === null ? null : `Against the ${spanText} before`;
}

export function SelectionPanel({
  selection,
  zoom,
  eventsHref,
}: Readonly<{ selection: StaffAnalyticsSelection; zoom: Zoom; eventsHref: string }>) {
  const change = comparison(selection, selection.previous);
  const total = selection.whereTimeWent.reduce((sum, row) => sum + row.seconds, 0);
  const note = previousNote(selection, spanPhrase(zoom));
  return (
    <Card className="rounded-2xl">
      <section aria-labelledby="explore-selection">
        <CardHeader className="pb-3">
          <CardTitle as="h2" id="explore-selection" className="text-xl leading-6">
            In this selection
          </CardTitle>
          {note === null ? null : (
            <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">{note}</p>
          )}
        </CardHeader>
        <CardContent>
          <dl className="m-0 grid grid-cols-2 gap-2.5">
            <Stat
              label="Sessions"
              value={appNumber(selection.sessions, countText)}
              change={change.sessions}
            />
            <Stat
              label="Time in app"
              value={appNumber(selection.timeInAppSeconds, durationText)}
              change={change.time}
            />
            <Stat label="Requests" value={countText(selection.requests)} change={change.requests} />
            <Stat label="Paid" value={moneyText(selection.amountPaidMinor)} change={change.paid} />
          </dl>
          {selection.availability === "OPTED_OUT" ? (
            <p className="m-0 text-[13px] leading-[18px] text-muted-foreground">
              The customer chose not to share app analytics, so sessions and time are not shown.
              Requests and payments come from records.
            </p>
          ) : null}
          {selection.whereTimeWent.length === 0 || total === 0 ? null : (
            <section aria-labelledby="explore-where-time" className="grid gap-2">
              <h3 id="explore-where-time" className="m-0 text-[13px] font-semibold">
                Where the time went
              </h3>
              <ul className="m-0 grid list-none gap-2 p-0">
                {selection.whereTimeWent.slice(0, 5).map((row) => (
                  <ShareBar
                    key={row.screen}
                    label={screenLabel(row.screen)}
                    share={row.seconds / total}
                  />
                ))}
              </ul>
            </section>
          )}
          <Link
            to={eventsHref}
            className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground no-underline hover:bg-link compact:min-h-9"
          >
            See the events in this range
          </Link>
        </CardContent>
      </section>
    </Card>
  );
}
